import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { setCurrentUser } from '@store/reducers/auth';
import { loginWithEmail, signInByGoogle } from '@app/services/auth';
import { useAppDispatch } from '@app/store/store';

import { useAuth } from '../../contexts/AuthContext';

const ASSETS = '/assets';

const gaussianThemes = ['theme1','theme2','theme3','theme4','theme5','theme6'];
const gradientThemes = ['theme7','theme8','theme9','theme10','theme11','theme12','theme13','theme14','theme15'];

const LoginCover = () => {
  const [showPassword,        setShowPassword]       = useState(false);
  const [isAuthLoading,       setAuthLoading]        = useState(false);
  const [isGoogleAuthLoading, setGoogleAuthLoading]  = useState(false);
  const [currentTheme,        setCurrentTheme]       = useState('theme1');
  const [switcherOpen,        setSwitcherOpen]       = useState(false);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { login: authLogin } = useAuth();

  useEffect(() => {
    document.body.className = `bg-theme bg-theme2`;
    return () => {
      document.body.className = 'bg-theme bg-theme2';
    };
  }, [currentTheme]);

  const login = async (email: string, password: string) => {
    try {
      setAuthLoading(true);
      await authLogin(email, password);
      toast.success('Login is succeed!');
      navigate('/');
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || 'Failed');
    } finally {
      setAuthLoading(false);
    }
  };

  const loginByGoogle = async () => {
    try {
      setGoogleAuthLoading(true);
      await signInByGoogle();
      toast.success('Login is succeed!');
      navigate('/');
    } catch (error: any) {
      toast.error(error.message || 'Failed');
    } finally {
      setGoogleAuthLoading(false);
    }
  };

  const { handleChange, values, handleSubmit, touched, errors } = useFormik({
    initialValues: { email: '', password: '' },
    validationSchema: Yup.object({
      email:    Yup.string().email('Invalid email address').required('Email is required'),
      password: Yup.string()
        .min(5,  'Must be at least 5 characters')
        .max(30, 'Must be 30 characters or less')
        .required('Password is required'),
    }),
    onSubmit: (vals) => { login(vals.email, vals.password); },
  });

  return (
    <div className="wrapper">
      <div className="section-authentication-cover">
        <div>
          <div className="row g-0">

            {/* Left cover image */}
            <div className="col-12 col-xl-7 col-xxl-8 auth-cover-left align-items-center justify-content-center d-none d-xl-flex">
              <div className="card shadow-none bg-transparent rounded-0 mb-0">
                <div className="card-body">
                  <img
                    src={`${ASSETS}/images/login-images/login-cover.svg`}
                    className="img-fluid auth-img-cover-login"
                    width="650"
                    alt=""
                  />
                </div>
              </div>
            </div>

            {/* Right form panel - REMOVED bg-light class to allow theme color to show */}
            <div className="col-12 col-xl-5 col-xxl-4 auth-cover-right align-items-center justify-content-center">
              <div className="card rounded-0 m-3 bg-transparent mb-0">
                <div className="card-body p-sm-5">
                  <div>

                    {/* Logo */}
                    <div className="mb-3 text-center">
                      <img src={`${ASSETS}/images/logo-icon.png`} width="60" alt="" />
                    </div>

                    {/* Heading */}
                    <div className="text-center mb-4">
                      <h5 className="text-white">EZY Lab Admin</h5>
                      <p className="mb-0 text-white">Please log in to your account</p>
                    </div>

                    {/* Form */}
                    <div className="form-body">
                      <form className="row g-3" onSubmit={handleSubmit}>

                        {/* Email */}
                        <div className="col-12">
                          <label htmlFor="inputEmailAddress" className="form-label text-white">
                            Email
                          </label>
                          <input
                            id="inputEmailAddress"
                            name="email"
                            type="email"
                            className={[
                              'form-control',
                              touched.email && errors.email  ? 'is-invalid' : '',
                              touched.email && !errors.email ? 'is-valid'   : '',
                            ].join(' ').trim()}
                            placeholder="jhon@example.com"
                            value={values.email}
                            onChange={handleChange}
                          />
                          {touched.email && errors.email && (
                            <div className="invalid-feedback">{errors.email}</div>
                          )}
                        </div>

                        {/* Password */}
                        <div className="col-12">
                          <label htmlFor="inputChoosePassword" className="form-label text-white">
                            Password
                          </label>
                          <div className="input-group" id="show_hide_password">
                            <input
                              id="inputChoosePassword"
                              name="password"
                              type={showPassword ? 'text' : 'password'}
                              className={[
                                'form-control border-end-0',
                                touched.password && errors.password  ? 'is-invalid' : '',
                                touched.password && !errors.password ? 'is-valid'   : '',
                              ].join(' ').trim()}
                              placeholder="Enter Password"
                              value={values.password}
                              onChange={handleChange}
                            />
                            <button
                              type="button"
                              className="input-group-text bg-transparent border-start-0"
                              onClick={() => setShowPassword((p) => !p)}
                            >
                              <i className={`bx ${showPassword ? 'bx-show' : 'bx-hide'}`} />
                            </button>
                          </div>
                          {touched.password && errors.password && (
                            <div className="invalid-feedback d-block">
                              {errors.password}
                            </div>
                          )}
                        </div>

                        {/* Remember Me & Forgot Password */}
                        <div className="col-md-6">
                          <div className="form-check form-switch">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="flexSwitchCheckChecked"
                            />
                            <label className="form-check-label text-white" htmlFor="flexSwitchCheckChecked">
                              Remember Me
                            </label>
                          </div>
                        </div>
                        {/* <div className="col-md-6 text-end">
                          <Link to="/forgot-password" className="text-white">Forgot Password ?</Link>
                        </div> */}

                        {/* Submit */}
                        <div className="col-12">
                          <div className="d-grid">
                            <button
                              type="submit"
                              className="btn btn-light"
                              disabled={isAuthLoading || isGoogleAuthLoading}
                            >
                              {isAuthLoading ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden="true"
                                  />
                                  Signing in...
                                </>
                              ) : 'Sign in'}
                            </button>
                          </div>
                        </div>

                        {/* Sign up link */}
                        {/* <div className="col-12">
                          <div className="text-center">
                            <p className="mb-0 text-white">
                              Don&apos;t have an account yet?{' '}
                              <Link to="/register" className="text-white">Sign up here</Link>
                            </p>
                          </div>
                        </div> */}

                      </form>
                    </div>

                    {/* Social login separator */}
                    {/* <div className="login-separater text-center mb-5">
                      <span className="text-white">OR SIGN IN WITH</span>
                      <hr className="text-white" />
                    </div> */}

                    {/* Social icons */}
                    {/* <div className="list-inline contacts-social text-center">
                      <button
                        type="button"
                        className="list-inline-item bg-light text-white border-0 rounded-3"
                        onClick={(e) => e.preventDefault()}
                      >
                        <i className="bx bxl-facebook" />
                      </button>
                      <button
                        type="button"
                        className="list-inline-item bg-light text-white border-0 rounded-3"
                        onClick={(e) => e.preventDefault()}
                      >
                        <i className="bx bxl-twitter" />
                      </button>
                      <button
                        type="button"
                        className="list-inline-item bg-light text-white border-0 rounded-3"
                        onClick={loginByGoogle}
                      >
                        {isGoogleAuthLoading
                          ? <span className="spinner-border spinner-border-sm" role="status" />
                          : <i className="bx bxl-google" />
                        }
                      </button>
                      <button
                        type="button"
                        className="list-inline-item bg-light text-white border-0 rounded-3"
                        onClick={(e) => e.preventDefault()}
                      >
                        <i className="bx bxl-linkedin" />
                      </button>
                    </div> */}

                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Theme Switcher */}
      <div className={`switcher-wrapper${switcherOpen ? ' switcher-toggled' : ''}`}>
        <div
          className="switcher-btn"
          onClick={() => setSwitcherOpen((p) => !p)}
          style={{ cursor: 'pointer' }}
        >
          <i className="bx bx-cog bx-spin" />
        </div>
        <div className="switcher-body">
          <div className="d-flex align-items-center">
            <h5 className="mb-0 text-uppercase">Theme Customizer</h5>
            <button
              type="button"
              className="btn-close ms-auto close-switcher"
              aria-label="Close"
              onClick={() => setSwitcherOpen(false)}
            />
          </div>
          <hr />
          <p className="mb-0">Gaussian Texture</p>
          <hr />
          <ul className="switcher">
            {gaussianThemes.map((id) => (
              <li
                key={id}
                id={id}
                onClick={() => setCurrentTheme(id)}
                style={{ cursor: 'pointer' }}
              />
            ))}
          </ul>
          <hr />
          <p className="mb-0">Gradient Background</p>
          <hr />
          <ul className="switcher">
            {gradientThemes.map((id) => (
              <li
                key={id}
                id={id}
                onClick={() => setCurrentTheme(id)}
                style={{ cursor: 'pointer' }}
              />
            ))}
          </ul>
        </div>
      </div>

    </div>
  );
};

export default LoginCover;